import React, { useEffect, useRef, useState } from "react";
import useRefetchOnFocus from "../../hooks/useRefetchOnFocus";
import { NavLink, useNavigate } from "react-router-dom";
import "./Navbar.scss";
import Button from "../Common/Button/Button";
import BellIcons from "../../Assets/Img/BellIcon";
import useLogout from "../../hooks/useLogout";
import { getUnreadCount } from "../../services/notificationService";
import SwitchRoleModal from "../Common/SwitchRoleModal/SwitchRoleModal";

interface NavbarProps {
  toggleDark?: () => void;
  darkMode?: boolean;
}

export default function Navbar({ toggleDark, darkMode }: NavbarProps) {
  const { handleLogout, loggingOut } = useLogout();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [switchRoleOpen, setSwitchRoleOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);

  const fetchUnreadCount = () => {
    setCountLoading(true);
    (getUnreadCount() as Promise<any>)
      .then((res: any) => {
        const count = res?.data?.data?.count ?? res?.data?.count ?? 0;
        setUnreadCount(count);
      })
      .catch(() => {})
      .finally(() => setCountLoading(false));
  };
  useRefetchOnFocus(fetchUnreadCount);
  useEffect(() => {
    fetchUnreadCount();
    const handler = () => {
      fetchUnreadCount();
    };
    window.addEventListener("notification-unread-count-changed", handler);
    return () =>
      window.removeEventListener("notification-unread-count-changed", handler);
  }, []);
  const profileRef = useRef<HTMLDivElement>(null);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const firstName: string = storedUser.firstName ?? "";
  const lastName: string = storedUser.lastName ?? "";
  const [userAvatar, setUserAvatar] = useState<string>(
    storedUser.avatar?.url ?? "",
  );
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "?";
  const displayName = firstName
    ? `${firstName.charAt(0).toUpperCase()}. ${lastName}`
    : "User";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "User";

  useEffect(() => {
    const handler = () => {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      setUserAvatar(u.avatar?.url ?? "");
    };
    window.addEventListener("avatar-changed", handler);
    return () => window.removeEventListener("avatar-changed", handler);
  }, []);

  useEffect(() => {
    if (!profileOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [profileOpen]);

  return (
    <>
      <nav className="navbar navbar-expand-lg bg-body-tertiary nav">
        <div className="container-fluid">
          <NavLink className="navbar-brand" to="/admin">
            <div className="logoArea">
              <div className="logoMark">
                <span className="lm1"></span>
                <span className="lm2"></span>
                <span className="lm3"></span>
                <span className="lm4"></span>
                <span className="lm5"></span>
                <span className="lm6"></span>
              </div>
              <div className="logoText">
                <div className="lt1">Consultant Services</div>
                <div className="lt2">
                  Lender Construction &amp; Project Consulting
                </div>
              </div>
            </div>
          </NavLink>
          <div className="d-flex d-lg-none align-items-center gap-2">
            <div
              className="bell"
              onClick={() => navigate("/notifications")}
              style={{ cursor: "pointer" }}
            >
              <BellIcons />
              {countLoading ? (
                <div className="bellbadgeWrapper">
                  <div className="bellbadge">
                    <span className="btn-spinner" />
                  </div>
                </div>
              ) : unreadCount > 0 ? (
                <div className="bellbadgeWrapper">
                  <div className="bellbadge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </div>
                </div>
              ) : null}
            </div>
            <Button
              className="navbar-toggler"
              aria-controls="navbarSupportedContent"
              aria-expanded={navOpen}
              aria-label="Toggle navigation"
              onClick={() => setNavOpen((o) => !o)}
            >
              <span className="navbar-toggler-icon"></span>
            </Button>
          </div>
          {/* Desktop nav */}
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 navlinks">
              <li className="nav-item">
                <NavLink
                  to="/admin"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Dashboard
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/add-new-project"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Add New Project
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/manage-users"
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Manage Users
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/manage-bank"
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Manage Banks
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/reports"
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Reports
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/inspector-assignment"
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  Inspector Assign
                </NavLink>
              </li>
            </ul>
            <div className="navBtnWrapper">
              <div className="navr">
                <Button className="dkbtn" onClick={toggleDark}>
                  {darkMode ? "Light mode" : "Dark mode"}
                </Button>
                <div
                  className="bell"
                  onClick={() => navigate("/notifications")}
                  style={{ cursor: "pointer" }}
                >
                  <BellIcons />
                  {countLoading ? (
                    <div className="bellbadgeWrapper">
                      <div className="bellbadge">
                        <span className="btn-spinner" />
                      </div>
                    </div>
                  ) : unreadCount > 0 ? (
                    <div className="bellbadgeWrapper">
                      <div className="bellbadge">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="profileWrap" ref={profileRef}>
                  <div className="ppill" onClick={() => navigate("/profile")}>
                    <div className="av">
                      {userAvatar ? (
                        <img src={userAvatar} alt={initials} />
                      ) : (
                        initials
                      )}
                    </div>
                    <span className="pn">{displayName}</span>
                    {/* <span className={`chv${profileOpen ? " open" : ""}`}></span> */}
                  </div>
                  <div
                    className={`profileDropdown${profileOpen ? " open" : ""}`}
                  >
                    <div className="pdHeader">
                      <div className="pdAv">{initials}</div>
                      <div className="pdInfo">
                        <div className="pdName">{fullName}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  className="dkbtn"
                  onClick={() => setSwitchRoleOpen(true)}
                >
                  Switch Role
                </Button>
                <Button
                  className="signoutBtn"
                  onClick={handleLogout}
                  disabled={loggingOut}
                >
                  {loggingOut && <span className="btn-spinner" />}{" "}
                  {loggingOut ? "Signing out" : "Sign Out"}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile side drawer */}
          {navOpen && (
            <div className="nav-overlay" onClick={() => setNavOpen(false)} />
          )}
          <div className={`nav-drawer${navOpen ? " open" : ""}`}>
            <div className="nd-header">
              <div className="nd-logo">
                <div className="logoMark">
                  <span className="lm1"></span>
                  <span className="lm2"></span>
                  <span className="lm3"></span>
                  <span className="lm4"></span>
                  <span className="lm5"></span>
                  <span className="lm6"></span>
                </div>
                <div className="nd-brand">
                  <div className="nd-b1">Consultant Services</div>
                  <div className="nd-b2">
                    Lender Construction & Project Consulting
                  </div>
                </div>
              </div>
              <Button
                className="nd-close"
                aria-label="Close menu"
                onClick={() => setNavOpen(false)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 1l12 12M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </Button>
            </div>

            <div className="nd-user" onClick={() => navigate("/profile")}>
              <div className="nd-av">{initials}</div>
              <div>
                <div className="nd-name">{displayName}</div>
                <div className="nd-role">Admin</div>
              </div>
            </div>

            <div className="nd-section-label">Navigation</div>
            <ul className="nd-links">
              {[
                { to: "/admin", label: "Dashboard", end: true },
                { to: "/add-new-project", label: "Add New Project", end: true },
                { to: "/manage-users", label: "Manage Users" },
                { to: "/reports", label: "Reports" },
                { to: "/inspector-assignment", label: "Inspector Assign" },
              ].map(({ to, label, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    onClick={() => setNavOpen(false)}
                    className={({ isActive }) =>
                      `nd-link${isActive ? " active" : ""}`
                    }
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="nd-footer">
              <Button className="nd-theme-btn" onClick={toggleDark}>
                <span>{darkMode ? "☀" : "☾"}</span>
                {darkMode ? "Light mode" : "Dark mode"}
              </Button>
              <Button
                className="nd-theme-btn"
                onClick={() => {
                  setNavOpen(false);
                  setSwitchRoleOpen(true);
                }}
              >
                Switch Role
              </Button>
              <Button
                className="nd-signout-btn"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                {loggingOut && <span className="btn-spinner" />}
                {loggingOut ? "Signing out…" : "Sign Out"}
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <SwitchRoleModal
        show={switchRoleOpen}
        onClose={() => setSwitchRoleOpen(false)}
      />
    </>
  );
}
